import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import { Cookies } from "react-cookie";
import { ConfigProvider } from "antd";
import { nanoid } from "nanoid";
import "./index.scss";
import App from "./App";
import i18n from "@tools/i18n";
import antLocale from "@tools/antLocale";
import { COOKIE, LOCALSTORAGE } from "@tools/constants";
import { GlobalProvider } from "@tools/globalContext";
import themeConfig from "@resources/themeConfig.json";
import pagesConfig from "@extension/config/pagesConfig";
import pagesDecorator from "@extension/config/pagesDecorator";
import LoginForm from "@components/LoginForm";
import { Api, User } from "@resources/api/Api";
import { initSocket } from "@tools/webSocket/webSocket";
import defaultSettings from "@resources/defaultSettings";
import { getStorage, setStorage } from "@tools/localStorage";
import EventEmitterRC from "@tools/eventEmitterContext/EventEmitterRC";
import AppFree, { pageWithoutLogin } from "./AppFree";
import { capitalizeFirstLetter } from "@tools/capitalizeFirstLetter";
import wait from "@tools/wait";

let root: any;

const getInitConfig = () => {
    return {
        themeConfig: themeConfig,
        pagesConfig: pagesConfig,
        pagesDecorator: pagesDecorator,
        notifications: {},
        userAgent: { isMobile: false },
        uploadUrl: process.env.REACT_APP_UPLOAD_URL || "",
        imageSource: process.env.REACT_APP_IMAGE_URL || "",
        hydraDirectory: process.env.REACT_APP_HYDRA_PATH || "",

        headerConfig: {
            showCurrencySwitch: true,
            showCountrySwitch: true,
            showGlobalSearch: true,
            showTimer: true,
        },
    };
};

const getUserCompany = async ({ user, api, lang }) => {
    if (!user.company?.id) return null;

    let companyRes: any = await api.company.readCompany({ id: user.company.id });

    if (companyRes?.error) {
        console.error(companyRes?.error);
        return null;
    }

    return companyRes?.data?.data || null;
};

const getUserWorkplace = async ({ user, api, lang }) => {
    if (!user.office?.id) return null;

    let workplaceRes: any = await api.office.readOffice({ id: user.office.id });

    if (workplaceRes?.error) {
        console.error(workplaceRes?.error);
        return null;
    }

    return workplaceRes?.data?.data || null;
};

const loadInitData = async (data: {
    user?: User | null;
    token: string | null;
    lang: string;
    currency: string;
    sid?: string;
}) => {
    let { token, user, lang, sid } = data;

    if (token && !user) {
        const userId = getStorage(LOCALSTORAGE.USER);
        const userIndex = getStorage(LOCALSTORAGE.USERINDEX) || "user";
        const api = new Api({ baseApiParams: { headers: { authorization: `Bearer ${token}` } } });

        try {
            const resData = await api[userIndex][`read${capitalizeFirstLetter(userIndex)}`]({ id: userId, lang });
            const result = resData.data;
            if (result.success) {
                const user = result.data;
                const company = await getUserCompany({ user, api, lang });
                const workplace = await getUserWorkplace({ user, api, lang });
                return { user, version: result.version, company, workplace };
            }
        } catch (e: any) {
            console.error(e);
            if (e.status === 500) {
                setTimeout(() => render({ sid }), 2000);
                return;
            } else {
                setStorage(LOCALSTORAGE.TOKEN, "");
                setStorage(LOCALSTORAGE.USER, "");
                render({ sid });
                return;
            }
        }
    }
    if (user) return { user };
    return {};
};

const onLogin = (data: { token?: string; user?: any }) => {
    if (data.token) setStorage(LOCALSTORAGE.TOKEN, data.token);
    if (data.user) {
        setStorage(LOCALSTORAGE.USER, data.user.id);
    }
    render(data.user);
};

const render = async (data: { user?: User; sid?: string }) => {
    let { user } = data;
    let content;
    const cookies = new Cookies();
    let lang = cookies.get(COOKIE.LANG);
    if (!defaultSettings.languages.includes(lang)) {
        lang = defaultSettings.lang;
        cookies.set(COOKIE.LANG, lang, { path: "/" });
    }

    let currency = cookies.get(COOKIE.CURRENCY);
    if (!defaultSettings.currency.includes(currency)) {
        currency = defaultSettings.defaultCurrency;
        cookies.set(COOKIE.CURRENCY, currency, { path: "/" });
    }

    const token = getStorage(LOCALSTORAGE.TOKEN);
    const sid = data.sid || nanoid(8);

    const initData = await loadInitData({ user, lang, currency, token, sid });
    if (!initData) return;

    process.env.REACT_APP_SENTRY_DSN && initData?.user && Sentry.setUser({
        id:initData.user.id,
        email: initData.user.email,
        username: initData.user.name,
    });
    initSocket({ token, sid });
    const serverConfig = getInitConfig();

    const antLocalization = await antLocale(lang);

    if (!token && !pageWithoutLogin()) {
        content = <LoginForm onLogin={onLogin} token={token} />;
    } else if (pageWithoutLogin()) {
        content = noLoginPage({}, antLocalization);
    } else if (initData?.user?.role?.id === "superAdmin") {
        content = superAdminContent(
            Object.assign({}, { user, token, currency, sid }, initData, serverConfig),
            antLocalization
        );
    } else if (initData?.user?.role?.id === "admin") {
        content = superAdminContent(
            Object.assign({}, { user, token, currency, sid }, initData, serverConfig),
            antLocalization
        );
    } else if (initData?.user?.role?.id === "user") {
        content = superAdminContent(
            Object.assign({}, { user, token, currency, sid }, initData, serverConfig),
            antLocalization
        );
    } else if (initData?.user?.role?.id === "contact") {
        content = clientContent(
            Object.assign({}, { user, token, currency, sid }, initData, serverConfig),
            antLocalization
        );
    }

    root.render(content);
};

const ANT_DESIGN_THEME_CONFIG = {
    components: {
        Layout: {
            siderBg: themeConfig.theme.colors.lightTheme.backgroundAlt,
            lightSiderBg: themeConfig.theme.primaryColor,
            headerBg: themeConfig.theme.colors.lightTheme.backgroundAlt,
        },
        Badge: {
            dotSize: 8
        },
        Menu: {
            itemSelectedBg: "transparent",
            itemSelectedColor: themeConfig.theme.primaryColor,
            itemColor: themeConfig.theme.colors.lightTheme.textSecondary,
        },
        Timeline: {
            dotBg: "transparent"
        }
    },
    cssVar: true,
    token: {
        colorPrimary: themeConfig.theme.primaryColor,
        fontFamily: themeConfig.theme.fontFamily,
    },
};

const superAdminContent = (configData, antLocalization) => (
    <GlobalProvider value={configData}>
        <ConfigProvider theme={ANT_DESIGN_THEME_CONFIG as any} locale={antLocalization}>
            <EventEmitterRC>
                <App />
            </EventEmitterRC>
        </ConfigProvider>
    </GlobalProvider>
);

const clientContent = (configData, antLocalization) => (
    <GlobalProvider
        value={Object.assign({}, configData, {
            headerConfig: {
                showCurrencySwitch: false,
                showCountrySwitch: true,
                showGlobalSearch: false,
                showTimer: false,
            },
            userMenuConfig: {
                showProfile: false,
            },
        })}
    >
        <ConfigProvider theme={ANT_DESIGN_THEME_CONFIG as any} locale={antLocalization}>
            <EventEmitterRC>
                <App />
            </EventEmitterRC>
        </ConfigProvider>
    </GlobalProvider>
);

const noLoginPage = (configData, antLocalization) => (
    <GlobalProvider value={configData}>
        <ConfigProvider theme={ANT_DESIGN_THEME_CONFIG as any} locale={antLocalization}>
            <EventEmitterRC>
                <AppFree />
            </EventEmitterRC>
        </ConfigProvider>
    </GlobalProvider>
);

window.onload = async () => {
     process.env.REACT_APP_SENTRY_DSN && Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        sendDefaultPii: true,
        tracesSampleRate: 1.0
    });

    root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
    let lang = new Cookies().get(COOKIE.LANG) || defaultSettings.lang;
    if (!defaultSettings.languages.includes(lang)) {
        lang = defaultSettings.lang;
        new Cookies().set(COOKIE.LANG, lang, { path: "/" });
    }
    let currency = new Cookies().get(COOKIE.CURRENCY) || defaultSettings.defaultCurrency;
    if (!defaultSettings.currency.includes(currency)) {
        currency = defaultSettings.defaultCurrency;
        new Cookies().set(COOKIE.CURRENCY, currency, { path: "/" });
        await wait(1000)
    }

    await i18n(lang);

    await render({});
};
